import React, { useState } from 'react';
import {
  Box, Typography, Button, Grid, Table, TableHead, TableBody, TableRow,
  TableCell, Paper, Collapse, IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend
} from 'recharts';



interface WarningEntry {
  id: string;
  tramId: number;
  message: string;
  warningType: string;
  timestamp: string;
  expectedTags: string[];
  missingTags: string[];
  extraTags: string[];
}

interface DetectorGroup {
  key: string;
  count: number;
  entries: WarningEntry[];
}

interface TramGroup {
  key: number;
  count: number;
  entries: WarningEntry[];
}

const extractDetector = (message: string) => {
  const match = message.match(/detector ([\w/-]+)/);
  return match ? match[1] : 'Unknown';
};


const WarningDashboard = ({ data }: { data: WarningEntry[] }) => {
  const [expanded, setExpanded] = useState<string | number | null>(null);
  const [tramChartView, setTramChartView] = useState<'chart' | 'table'>('chart');
  const [detectorChartView, setDetectorChartView] = useState<'chart' | 'table'>('chart');

  const detectorGrouped: DetectorGroup[] = Object.entries(
    data.reduce((acc, entry) => {
      const detector = extractDetector(entry.message);
      acc[detector] = acc[detector] || [];
      acc[detector].push(entry);
      return acc;
    }, {} as Record<string, WarningEntry[]>)
  ).map(([key, entries]) => ({ key, count: entries.length, entries }))
    .sort((a, b) => b.count - a.count).slice(0, 10);

  const tramGrouped: TramGroup[] = Object.entries(
    data.reduce((acc, entry) => {
      acc[entry.tramId] = acc[entry.tramId] || [];
      acc[entry.tramId].push(entry);
      return acc;
    }, {} as Record<number, WarningEntry[]>)
  ).map(([key, entries]) => ({ key: Number(key), count: entries.length, entries }))
    .sort((a, b) => b.count - a.count).slice(0, 10);

  const toggleExpanded = (key: string | number) =>
    setExpanded(prev => (prev === key ? null : key));
  
const topTramIds = tramGrouped.map(t => t.key);
const detectorTramMap: Record<string, Record<number, number>> = {};

data.forEach(entry => {
  if (!topTramIds.includes(entry.tramId)) return;

  const detector = extractDetector(entry.message);
  if (!detector || detector === 'Unknown') return;

  if (!detectorTramMap[detector]) detectorTramMap[detector] = {};
  detectorTramMap[detector][entry.tramId] = (detectorTramMap[detector][entry.tramId] || 0) + 1;
});

const heatmapJson = Object.entries(detectorTramMap).map(([detector, tramCounts]) => {
  const row: { detector: string; [tramId: number]: number } = { detector };
  topTramIds.forEach(id => {
    row[id] = tramCounts[id] || 0;
  });
  return row;
});
console.log("HeatMap",heatmapJson)
  return (
    <Box sx={{height:"100vh", overflowY:"auto", width:"100%"}}p={3}>
      <Typography variant="h5" gutterBottom> Tram Detector Warnings Dashboard</Typography>

      {/* DETECTOR SECTION */}
      <Box mt={4}>
        <Grid container justifyContent="space-between">
          <Typography variant="h6">Top 10 Worst Detectors</Typography>
          <Button onClick={() => setDetectorChartView(prev => prev === 'chart' ? 'table' : 'chart')}>
            {detectorChartView === 'chart' ? 'Show Table' : 'Show Chart'}
          </Button>
        </Grid>

        {detectorChartView === 'chart' ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={detectorGrouped.map(d => ({ name: d.key, value: d.count }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#1976d2" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Detector</TableCell>
                  <TableCell>Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detectorGrouped.map(d => (
                  <React.Fragment key={d.key}>
                    <TableRow>
                      <TableCell>
                        <IconButton onClick={() => toggleExpanded(d.key)}>
                          <ExpandMoreIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>{d.key}</TableCell>
                      <TableCell>{d.count}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} style={{ padding: 0 }}>
                        <Collapse in={expanded === d.key}>
                          <Box height={200} px={2}>
                            <ResponsiveContainer>
                              <BarChart data={Object.entries(
                                d.entries.reduce((acc, e) => {
                                  const id = e.tramId.toString().replace(/^10/, '');
                                  acc[id] = (acc[id] || 0) + 1;
                                  return acc;
                                }, {} as Record<string, number>)
                              ).map(([id, count]) => ({ id, count }))}>
                                <XAxis dataKey="id" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#8884d8" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {/* TRAM SECTION */}
      <Box mt={6}>
        <Grid container justifyContent="space-between">
          <Typography variant="h6">Top 10 Trams with Most Warnings</Typography>
          <Button onClick={() => setTramChartView(prev => prev === 'chart' ? 'table' : 'chart')}>
            {tramChartView === 'chart' ? 'Show Table' : 'Show Chart'}
          </Button>
        </Grid>

        {tramChartView === 'chart' ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tramGrouped.map(t => ({
              name: t.key.toString().replace(/^10/, ''),
              value: t.count
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#ff9800" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Tram ID</TableCell>
                  <TableCell>Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tramGrouped.map(t => (
                  <React.Fragment key={t.key}>
                    <TableRow>
                      <TableCell>
                        <IconButton onClick={() => toggleExpanded(t.key)}>
                          <ExpandMoreIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>{t.key.toString().replace(/^10/, '')}</TableCell>
                      <TableCell>{t.count}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} style={{ padding: 0 }}>
                        <Collapse in={expanded === t.key}>
                          <Box height={200} px={2}>
                            <ResponsiveContainer>
                              <BarChart data={Object.entries(
                                t.entries.reduce((acc, e) => {
                                  const det = extractDetector(e.message);
                                  acc[det] = (acc[det] || 0) + 1;
                                  return acc;
                                }, {} as Record<string, number>)
                              ).map(([id, count]) => ({ id, count }))}>
                                <XAxis dataKey="id" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#ffa726" />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      {/* HEATMAP */}
<Box mt={6}>
  <Typography variant="h6" gutterBottom>
    Heatmap: Detector Warnings Across Top 10 Trams
  </Typography>
  <Box sx={{ overflowX: 'auto' }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>Detector</TableCell>
          {Object.keys(heatmapJson[0] || {})
            .filter((key) => key !== 'detector')
            .map((tramId) => (
              <TableCell key={tramId} align="center">
                {tramId.toString().replace(/^10/, '')}
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {heatmapJson.map((row) => (
          <TableRow key={row.detector}>
            <TableCell>{row.detector}</TableCell>
            {Object.keys(row)
              .filter((tramId) => tramId !== 'detector')
              .map((tramId) => {
                const value = row[tramId];
                const getHeatColor = (value: number) => {
                  if (value === 0) return '#ffffff';
                  if (value === 1) return '#ffe082';
                  if (value === 2) return '#ffb74d';
                  if (value === 3) return '#ff8a65';
                  if (value >= 4) return '#e53935';
                  return '#ffffff';
                };
                const color = getHeatColor(value);
                return (
                  <TableCell
                    key={tramId}
                    align="center"
                    sx={{
                      backgroundColor: color,
                      width: 30,
                      height: 30,
                      padding: 0,
                      border: '1px solid #ddd',
                      color: value > 3 ? '#fff' : '#000',
                    }}
                  >
                    {value > 0 ? value : ''}
                  </TableCell>
                );
              })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
</Box>
    </Box>
  );
};

export default WarningDashboard;
